import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { RoutingStateService } from 'src/app/services/routing-state.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { ErrorService } from 'src/app/services/error.service';
import { ReportService } from 'src/app/services/report.service';
import { environment } from 'src/environments/environment';
import * as ReportActions from '../actions/report.actions';
import { AppState } from '../app.reducer';

@Injectable()
export class ReportEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private reportService: ReportService,
    private router: Router,
    private errorService: ErrorService,
    private routingStateService: RoutingStateService
  ) {}

  downloadReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.downloadReport),
      switchMap(({ payload }) => {
        this.store.dispatch(
          ReportActions.loading({ payload: { loading: true } })
        );
        return this.reportService
          .downloadReport(
            payload.reportId,
            payload.language,
            payload.parameters
          )
          .pipe(
            mergeMap((response) => {
              const parsed = JSON.parse(response.data);
              return of(
                ReportActions.reportDownloaded({
                  payload: { downloadedReport: parsed.base64 },
                }),
                ReportActions.loading({ payload: { loading: false } })
              );
            }),
            catchError((error) => {
              return of(
                ReportActions.actionFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  createReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.createReport),
      switchMap(({ payload }) => {
        this.store.dispatch(
          ReportActions.loading({ payload: { loading: true } })
        );
        return this.reportService.createReport(payload.report).pipe(
          map((response) => ({ payload, response })),
          mergeMap(() => {
            this.store.dispatch(ReportActions.reportCreated());
            return of(ReportActions.loading({ payload: { loading: false } }));
          }),
          catchError((error) => {
            return of(
              ReportActions.actionFail({
                payload: { error: this.errorService.mapError(error) },
              })
            );
          })
        );
      })
    )
  );

  getReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.readReports),
      switchMap(() => {
        this.store.dispatch(
          ReportActions.loading({ payload: { loading: true } })
        );

        return this.reportService.readReports().pipe(
          mergeMap((response) => {
            this.store.dispatch(
              ReportActions.reportsRead({
                payload: {
                  reports: response.data,
                },
              })
            );
            return of(ReportActions.loading({ payload: { loading: false } }));
          }),
          catchError((error) => {
            return of(
              ReportActions.actionFail({
                payload: { error: this.errorService.mapError(error) },
              })
            );
          })
        );
      })
    )
  );

  getReportById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.readReportById),
      switchMap(({ payload }) => {
        return this.reportService.readReportById(payload.reportId).pipe(
          mergeMap((value) => {
            return of(
              ReportActions.reportByIdRead({ payload: { report: value.data } })
            );
          }),
          catchError((error) => {
            this.store.dispatch(ReportActions.redirectToReportsList());
            return of(
              ReportActions.actionFail({
                payload: { error: this.errorService.mapError(error) },
              })
            );
          })
        );
      })
    )
  );

  redirectToReportsList$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportActions.redirectToReportsList),
        tap(async () => {
          const previousUrl = this.routingStateService.getPreviousUrl();
          await this.router.navigate([previousUrl ?? '/reports']);
        })
      ),
    { dispatch: false }
  );

  updateReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.updateReport),
      switchMap(({ payload }) => {
        this.store.dispatch(
          ReportActions.loading({ payload: { loading: true } })
        );
        return this.reportService.updateReport(payload.report).pipe(
          map((response) => ({ payload, response })),
          mergeMap(() => {
            this.store.dispatch(ReportActions.reportUpdated());
            return of(ReportActions.loading({ payload: { loading: false } }));
          }),
          catchError((error) => {
            if (environment.optimisticLockErrors.includes(error.error?.name))
              return of(
                ReportActions.optimisticLockError({
                  payload: {
                    optimisticLockError: this.errorService.mapError(error),
                  },
                })
              );
            else
              return of(
                ReportActions.actionFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
          })
        );
      })
    )
  );

  deleteReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.deleteReport),
      switchMap(({ payload }) => {
        this.store.dispatch(
          ReportActions.loading({ payload: { loading: true } })
        );
        return this.reportService
          .deleteReport(payload.reportId, payload.version)
          .pipe(
            mergeMap(() => {
              this.store.dispatch(ReportActions.reportDeleted());
              return of(ReportActions.loading({ payload: { loading: false } }));
            }),
            catchError((error) => {
              if (environment.optimisticLockErrors.includes(error.error?.name))
                return of(
                  ReportActions.optimisticLockError({
                    payload: {
                      optimisticLockError: this.errorService.mapError(error),
                    },
                  })
                );
              else
                return of(
                  ReportActions.actionFail({
                    payload: { error: this.errorService.mapError(error) },
                  })
                );
            })
          );
      })
    )
  );
}
